import { compareDesc } from 'date-fns';
import { useMemo } from 'react';

import type {
  PlayerCoachNote,
  PlayerKitClub,
} from '@/features/guardian-player/components/kit-notes/types';
import { parseNoteDate } from '@/features/guardian-player/components/kit-notes/utils';
import { useGuardianPlayerDetailQuery } from '@/features/guardian-player/queries';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';

/**
 * Data for the KIT & Notes screen. Coach notes and the welcome kit live on the player
 * profile doc; the nav-param player is the fallback so notes render instantly while
 * the profile query is in flight.
 */
export function usePlayerKitNotes(player: GuardianPlayer) {
  const { data: serverPlayer, isLoading } = useGuardianPlayerDetailQuery(player.id);

  const notes = useMemo((): PlayerCoachNote[] => {
    const source = serverPlayer?.notes ?? player.notes ?? [];
    return [...source].sort((a, b) => compareDesc(parseNoteDate(a.date), parseNoteDate(b.date)));
  }, [serverPlayer?.notes, player.notes]);

  const kitClubs = useMemo((): PlayerKitClub[] => {
    const source = serverPlayer?.welcomeKit ?? [];
    // Newest kit first — the design lists provided kits from the highest level down.
    return [...source].sort((a, b) => (b.level ?? 0) - (a.level ?? 0));
  }, [serverPlayer?.welcomeKit]);

  return { notes, kitClubs, isLoading };
}
